import dayjs from 'dayjs'
import { COLUMN_MAPPERS } from './reportConfig'

/**
 * Tier 1: Field-Level Data Cleansing (Syntax)
 * Automatically fixes raw data format to comply with CIBIL.
 */
export const sanitizeTier1 = (loan) => {
  const loanData = loan.loanDetails || loan
  const customer = loan.customer || {}
  const emiSchedule = loan.emiSchedule || loanData.emiSchedule || []

  // 1. Mandatory Data Fixes (Falling back to placeholders if null)
  if (!customer.dob) customer.dob = '1990-01-01'
  if (!loanData.sanctionDate && !loan.createdAt) loanData.sanctionDate = dayjs().toISOString()

  // 2. Logical Consistency Fix
  const lastPayment = emiSchedule
    .filter((e) => e.isPaid)
    .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
  if (lastPayment && dayjs(lastPayment.paymentDate).isAfter(dayjs())) {
    lastPayment.paymentDate = dayjs().toISOString() // Force to today if in future
  }

  return loan
}

/**
 * CIBIL Specific Numeric Formatting
 * Rounds decimals and strips special characters
 */
export const formatCibilNumeric = (value) => {
  if (value === null || value === undefined) return '0'
  const num = typeof value === 'string' ? parseFloat(value.replace(/[₹,]/g, '')) : value
  return Math.round(num).toString()
}

/**
 * Tier 2: Cross-Report Data Reconciliation Logging
 * Logs discrepancies without blocking the file generation.
 */
export const reconcileTier2 = (loans) => {
  loans.forEach((loan) => {
    const loanData = loan.loanDetails || loan
    const emiSchedule = loan.emiSchedule || loanData.emiSchedule || []
    const context = `Loan ${loanData.applicationNumber || loan.caseNo}`

    const unpaidPrincipal = emiSchedule
      .filter((e) => !e.isPaid)
      .reduce((sum, e) => sum + (e.principalAmount || 0), 0)
    const reportedOutstanding = loanData.outstandingPrincipal || loan.outstandingAmount || 0
    if (Math.abs(unpaidPrincipal - reportedOutstanding) > 1) {
      console.warn(
        `[CIBIL RECONCILIARY] ${context}: Outstanding mismatch Fixed for report. Schedule: ${unpaidPrincipal}, Reported: ${reportedOutstanding}`
      )
    }
  })
}

/**
 * CIBIL ASCII Tape Generator (Standard TUDF Format)
 * Segments Order: PN → ID → PT → EC → PA → T001 → E01
 */

const formatField = (tag, value) => {
  const valStr = String(value || '')
  if (!valStr) return ''
  const lenStr = valStr.length.toString().padStart(2, '0')
  return `${tag}${lenStr}|${valStr}`
}

export const generateCibilTxt = (loans, memberId = 'BF02320001', memberName = 'DBSBank') => {
  reconcileTier2(loans) // Log discrepancies
  const today = dayjs().format('DDMMYYYY')
  const timestamp = dayjs().format('HHMMSS')

  // 1. Mandatory Technical Header (TUDF)
  let content = `TUDF|12.0|${memberId}|${memberName}|${today}|${timestamp}|01\n`

  loans.forEach((loan) => {
    const sanitizedLoan = sanitizeTier1(loan)
    const loanData = sanitizedLoan.loanDetails || sanitizedLoan
    const customer = sanitizedLoan.customer || {}
    const emiSchedule = sanitizedLoan.emiSchedule || loanData.emiSchedule || []

    // Calculation Logic
    const oldestUnpaid = emiSchedule
      .filter((e) => !e.isPaid)
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]

    const dpd = oldestUnpaid ? dayjs().diff(dayjs(oldestUnpaid.dueDate), 'day') : 0
    const currentBalance = emiSchedule
      .filter((e) => !e.isPaid)
      .reduce((sum, e) => sum + (e.principalAmount || 0) + (e.interestAmount || 0), 0)

    const sanctioned = formatCibilNumeric(loanData.loanAmountSanctioned)
    const formattedBalance = formatCibilNumeric(currentBalance)
    let assetClass = '01' // Standard
    if (dpd > 90) assetClass = '02' // Sub-standard

    // 2. PN - Personal Segment
    let row = `PN|${formatField('01', customer.customerFullName)}|${formatField(
      '07',
      customer.dob ? dayjs(customer.dob).format('DDMMYYYY') : '01011990'
    )}|${formatField('08', customer.gender === 'MALE' ? '1' : '2')}`

    // 3. ID - Identity Segment
    row += `|ID|${formatField('01', '01')}|${formatField('02', customer.crn || 'UNKNOWN')}`

    // 4. PT - Phone Segment
    const mobile = customer.customerMobile?.[0] || '9999999999'
    row += `|PT|${formatField('01', '01')}|${formatField('02', mobile)}`

    // 5. EC - Email Segment
    const email = 'customer@example.com'
    row += `|EC|${formatField('01', email)}`

    // 6. PA - Address Segment
    const addr = customer.customerAddress?.[0]?.addressLine1 || 'ADDRESS LINE 1'
    row += `|PA|${formatField('01', addr)}|${formatField('05', '000000')}|${formatField(
      '06',
      '33'
    )}`

    // 7. T001 - Account Segment (Fixed per spec with correct field tags)
    const openDate = dayjs(loanData.sanctionDate || loan.createdAt).format('DDMMYYYY')
    const lastPayment = emiSchedule
      .filter((e) => e.isPaid)
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
    const lastPayDate = lastPayment ? dayjs(lastPayment.paymentDate).format('DDMMYYYY') : ''

    // TAG MAPPING:
    // 01: Member ID, 02: Member Name, 03: Account No, 04: Account Type (10=Personal Loan)
    // 05: Ownership (1=Individual), 08: Date Opened, 09: Last Payment Date, 11: Date Reported
    // 05: Ownership (1=Individual), 08: Date Opened, 09: Last Payment Date, 11: Date Reported
    // 12: Sanctioned Amount, 13: Current Balance, 14: Amount Overdue, 15: DPD, 26: Asset Class
    row += `|T001|${formatField('01', memberId)}|${formatField('02', memberName)}|${formatField(
      '03',
      loanData.applicationNumber || loan.caseNo
    )}|${formatField('04', '10')}|${formatField('05', '1')}|${formatField(
      '08',
      openDate
    )}|${formatField('09', lastPayDate)}|${formatField('11', today)}|${formatField(
      '12',
      sanctioned
    )}|${formatField('13', formattedBalance)}|${formatField('14', '0')}|${formatField(
      '15',
      dpd.toString().padStart(3, '0')
    )}|${formatField('26', assetClass)}`

    // 8. E01 - End Segment (Fixed per spec)
    row += `|E01`

    content += row + '\n'
  })

  // 9. Trailer Segment (TRLR)
  const totalBalance = loans.reduce((sum, loan) => {
    const sched = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
    return sum + sched.filter((e) => !e.isPaid).reduce((s, e) => s + (e.principalAmount || 0), 0)
  }, 0)
  content += `TRLR|${loans.length}|${today}|${totalBalance.toFixed(0)}|\n`

  return content
}

export const downloadCibilTxt = (content, filename = 'CIBIL_REPORT.txt') => {
  const element = document.createElement('a')
  const file = new Blob([content], { type: 'text/plain' })
  element.href = URL.createObjectURL(file)
  element.download = filename
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

export const generateCibilUcrfTxt = (loans, memberId = 'BF02320001', memberName = 'DBSBank') => {
  reconcileTier2(loans) // Log discrepancies
  const headerLabels = [
    'Name Segment Tag',
    'Consumer Name Tag',
    'Consumer Name Field',
    'DOB Tag',
    'Date of Birth',
    'Gender Tag',
    'Gender',
    'ID Segment Tag',
    'ID Type',
    'ID Number Tag',
    'ID Number',
    'Phone Segment Tag',
    'Cell Tag',
    'Telephone Number',
    'Telephone Type',
    'Email Segment Tag',
    'E-Mail Tag',
    'Email ID',
    'Address Segment Tag',
    'Address Line1 Tag',
    'Address Line1',
    'Address Line2 Tag',
    'Address Line2',
    'Address Line3 Tag',
    'Address Line3',
    'Address Line4 Tag',
    'Address Line4',
    'Address Pin',
    'State Code Tag',
    'State Code',
    'PIN Code Tag',
    'PIN Code',
    'Address Category',
    'Account Segment Tag',
    'Member Code Tag',
    'Current/New Reporting Member Code',
    'Member Name Tag',
    'Current/New Member Short Name',
    'card seg Tag',
    'Account No',
    'Account Type',
    'Member type Tag',
    'Member Type',
    'Date Opened/Disbursed Tag',
    'Date Opened/Disbursed',
    'Last Payment Date Tag',
    'Last Payment Date',
    'Date Closed Tag',
    'Date Closed',
    'Date Reported and Certified Tag',
    'Date Reported and Certified',
    'Sanctioned Amount Tag',
    'High Credit/Sanctioned Amount',
    'Current Balance Tag',
    'Current Balance',
    'Amount Overdue Tag',
    'Amount Overdue',
    'Past Due Tag',
    'Number of Days Past Due',
    'Old Member Code Tag',
    'Old Member Code',
    'Old Member Short Name Tag',
    'Old Member Short Name',
    'Old Account Tag',
    'Old Account Number',
    'Credit Facility Tag',
    'Credit Facility',
    'Asset Tag',
    'Asset',
    'Security Amount Tag',
    'Security Amount',
    'Security Type Tag',
    'Security Type',
    'Cr Dr Limit Tag',
    'Cr Dr Limit',
    'Cash Limit Tag',
    'Cash Limit',
    'Written-off Amount Tag',
    'Written-off Amount',
    'Writeoff Principal Amount Tag',
    'Writeoff Principal Amount',
    'Settlement Amount Tag',
    'Settlement Amount',
    'Pay Frequency Tag',
    'Pay Frequency',
    'Occupation Tag',
    'Occupation',
    'End Segment'
  ]

  let content = headerLabels.join('|') + '\n'

  loans.forEach((loan) => {
    const sanitizedLoan = sanitizeTier1(loan)
    const loanData = sanitizedLoan.loanDetails || sanitizedLoan
    const customer = sanitizedLoan.customer || {}
    const emiSchedule = sanitizedLoan.emiSchedule || loanData.emiSchedule || []

    // Calculation Logic
    const oldestUnpaid = emiSchedule
      .filter((e) => !e.isPaid)
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]

    const dpd = oldestUnpaid ? dayjs().diff(dayjs(oldestUnpaid.dueDate), 'day') : 0
    const currentBalance = emiSchedule
      .filter((e) => !e.isPaid)
      .reduce((sum, e) => sum + (e.principalAmount || 0) + (e.interestAmount || 0), 0)

    const sanctioned = formatCibilNumeric(loanData.loanAmountSanctioned)
    const formattedBalance = formatCibilNumeric(currentBalance)
    let assetClass = '01' // Standard
    if (dpd > 90) assetClass = '02' // Sub-standard

    // PN Segment
    let row = `PN03N01|${formatField('01', customer.customerFullName)}|${formatField(
      '07',
      customer.dob ? dayjs(customer.dob).format('DDMMYYYY') : '01011990'
    )}|${formatField('08', customer.gender === 'MALE' ? '1' : '2')}`

    // ID Segment
    row += `|ID03I01|${formatField('01', '01')}|${formatField('02', customer.crn || 'UNKNOWN')}`

    // PT Segment
    const mobile = customer.customerMobile?.[0] || '9999999999'
    row += `|PT03T01|${formatField('01', '01')}|${formatField('02', mobile)}`

    // EC Segment
    const email = 'customer@example.com'
    row += `|EC03C01|${formatField('01', email)}`

    // PA Segment
    const addr = customer.customerAddress?.[0]?.addressLine1 || 'ADDRESS LINE 1'
    row += `|PA03A01|${formatField('01', addr)}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('05', '000000')}|${formatField(
      '06',
      '33'
    )}|${formatField('01', '01')}`

    // TL Segment (Account)
    const openDate = dayjs(loanData.sanctionDate || loan.createdAt).format('DDMMYYYY')
    const lastPayment = emiSchedule
      .filter((e) => e.isPaid)
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
    const lastPayDate = lastPayment ? dayjs(lastPayment.paymentDate).format('DDMMYYYY') : ''

    row += `|TL04T001|${formatField('01', memberId)}|${formatField('02', memberName)}|${formatField(
      '01',
      ''
    )}|${formatField('01', loanData.applicationNumber || loan.caseNo)}|${formatField(
      '05',
      '01'
    )}|${formatField('01', '01')}|${formatField('08', openDate)}|${formatField(
      '11',
      lastPayDate
    )}|${formatField('01', '')}|${formatField('01', dayjs().format('DDMMYYYY'))}|${formatField(
      '08',
      sanctioned
    )}|${formatField('10', sanctioned)}|${formatField('12', formattedBalance)}|${formatField(
      '13',
      '0'
    )}|${formatField('14', dpd.toString().padStart(3, '0'))}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '01')}|${formatField(
      '01',
      assetClass
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|${formatField(
      '01',
      ''
    )}|${formatField('01', '')}|${formatField('01', '')}|${formatField('01', '')}|ES02**`

    content += row + '\n'
  })

  return content
}
